/****************************************************************************************
 * RecordingDetectionsSVG.ts
 * -----------------------------------------------------------------------------
 * Lightweight class that turns one JSON "recording" object into
 * an inline-SVG string.  Import it, feed it the data, drop the string into the DOM (or
 * wrap in a React component—see bottom).
 *
 *	Usage (React)
 *	─────────────
 *		import { RecordingDetectionsSVG, RecordingSVG } from './RecordingDetectionsSVG';
 *		…
 *		<RecordingSVG record={recordObj} />;
 ****************************************************************************************/

// ────────────────────────────────────────────────────────────────────────────────
// CONFIG (edit freely)
// ────────────────────────────────────────────────────────────────────────────────
export const PX_PER_SEC = 1; //0.5;					// 2 s = 1 px  (vertical)
const TIMELINE_X = 60;					// x-pos of the vertical axis
const CONF_WIDTH = 60;				// width of the music confidence bar (horizontal)
export const TOP_PAD = 20;
const LAYER_PAD = 3;					// gap between coloured bars
const RECT_W = 14;					// width of each detection bar
const DET_START_X = TIMELINE_X + CONF_WIDTH + LAYER_PAD;
const LABEL_START_X = DET_START_X + 3 * (RECT_W + LAYER_PAD) + 10;	// x-pos of labels
const LABEL_PAD = 45;				// gap between label and bar

const COLORS = {
	timeline: '#000000',
	conf: '#9dd8ff',
	watermark: '#800080',
	nfp: '#d62828',
};

// ────────────────────────────────────────────────────────────────────────────────
// CLASS
// ────────────────────────────────────────────────────────────────────────────────
export default class RecordingDetectionsSVG {
	private durationSec = 1800;					// fixed 30 min

	constructor(
		private record: any,
		private pxPerSec = PX_PER_SEC
	) { }

	// public façade
	build(): string {
		const colLabels = ['DIGI', 'NFP1', 'NFP2'];	// column labels

		const h = TOP_PAD + this.y(this.durationSec);
		const w = TIMELINE_X + CONF_WIDTH + colLabels.length * (RECT_W + LAYER_PAD) + 350;

		let out: string[] = [
			`<svg xmlns = "http://www.w3.org/2000/svg" width = "${w}" height = "${h + 40}" font - family="sans-serif" > `,
			this.musicConfidencePolygon(h),
			this.line(TIMELINE_X, TOP_PAD, TIMELINE_X, h, COLORS.timeline, 2)
		];

		// minute ticks
		for (let s = 0; s <= this.durationSec; s += 60) {
			const y = this.y(s) + TOP_PAD;
			out.push(
				this.line(TIMELINE_X - 4, y, TIMELINE_X, y),
				this.text(TIMELINE_X - 36, y, String(s), 'end')
			);
		}

		// column labels
		for (let i = 0; i < colLabels.length; i++) {
			const x = RECT_W + DET_START_X + i * (RECT_W + LAYER_PAD);
			out.push(this.text(x, TOP_PAD - 10, colLabels[i], 'middle', -45));
		}

		this.paintLayer(this.grabWatermarks(), 0, COLORS.watermark, out);
		this.paintLayer(this.grabNfp(1), 1, '#CCCCCC', out);   // classification 1 (less confident) - grey
		this.paintLayer(this.grabNfp(2), 2, COLORS.nfp, out);  // classification 2 (more confident) - red

		out.push('</svg>');
		return out.join('\n');
	}

	// ───────── helpers ─────────
	private y = (s: number) => s * this.pxPerSec;

	private line = (x1: number, y1: number, x2: number, y2: number, c = COLORS.timeline, w = 1) =>
		`<line x1 = "${x1}" y1 = "${y1}" x2 = "${x2}" y2 = "${y2}" stroke = "${c}" stroke-width="${w}" style="z-index: 1;" /> `;

	private rect = (x: number, y: number, h: number, c: string, rx = 3, ry = 3) =>
		`<rect x = "${x}" y = "${y}" width = "${RECT_W}" height = "${h}" fill = "${c}" rx="${rx}" ry="${ry}" stroke="white" stroke-width="1" /> `;

	private text = (x: number, y: number, txt: string, anchor = 'start', rotate = 0) =>
		`<text x="${x}" y="${y}" font-size="10" text-anchor="${anchor}" dominant-baseline="middle" transform="rotate(${rotate} ${x} ${y})" style="z-index: 10;" fill="black" stroke="white" stroke-width="2.5" paint-order="stroke" stroke-linejoin="round"> ${txt} </text>`;

	private musicConfidencePolygon(h: number): string {
		const musicConfidence = this.record.nfp?.music_confidence;
		if (!musicConfidence || !musicConfidence.length) {
			return '';
		}
		// Array of numbers, one per second (index = time in seconds)
		const pts = musicConfidence.map((confidence: number, time: number) =>
			`${TIMELINE_X + confidence * CONF_WIDTH},${this.y(time) + TOP_PAD}`
		);
		pts.push(`${TIMELINE_X},${h}`, `${TIMELINE_X},${TOP_PAD}`);
		return `<polygon points="${pts.join(' ')}" fill="${COLORS.conf}" opacity="0.7"/>`;
	}

	private paintLayer(list: any[], idx: number, color: string, out: string[]) {
		const x = DET_START_X + idx * (RECT_W + LAYER_PAD);
		const gap = 20; // minimum gap between labels
		const xLbl = LABEL_START_X + (idx * LABEL_PAD);
		const xLine = xLbl - 2;

		// First pass: draw all detection bars
		list.forEach(m => {
			const yBar = this.y(m.start) + TOP_PAD;
			const h = Math.max(this.y(m.end - m.start), 2);
			out.push(this.rect(x, yBar, h, color));
		});

		// Second pass: group labels by start position (not by bar overlap)
		let lastLblY = -1e9;
		let pendingGroup: any[] = [];
		let groupStartY = 0;

		const flushGroup = () => {
			if (pendingGroup.length === 0) return;

			const first = pendingGroup[0];
			// Strip period and everything after from id
			let displayId = (first.id + '').includes('.') ? first.id.split('.')[0] : first.id;

			if (pendingGroup.length === 1) {
				// Single item - show normally
				const infoJson = JSON.stringify(first).replace(/"/g, '&quot;');
				out.push(
					this.line(x + RECT_W, groupStartY, xLine, groupStartY, color),
					`<text transform="rotate(30 ${xLbl} ${groupStartY})" x="${xLbl}" y="${groupStartY}" font-size="10" data-seek-time="${first.start}" data-detection-info="${infoJson}" style="cursor: pointer;">${displayId} (${(first.end - first.start).toFixed(0)})</text>`
				);
			} else {
				// Multiple items - show first + "more"
				const infoJson = JSON.stringify(pendingGroup).replace(/"/g, '&quot;');
				out.push(
					this.line(x + RECT_W, groupStartY, xLine, groupStartY, color),
					`<text transform="rotate(30 ${xLbl} ${groupStartY})" x="${xLbl}" y="${groupStartY}" font-size="10" data-seek-time="${first.start}" data-detection-info="${infoJson}" style="cursor: pointer;">${displayId} +${pendingGroup.length - 1} more</text>`
				);
			}
			pendingGroup = [];
		};

		list.forEach(m => {
			const yBar = this.y(m.start) + TOP_PAD;

			if (yBar - lastLblY < gap) {
				// Too close to last label - add to pending group
				pendingGroup.push(m);
			} else {
				// Far enough - flush previous group and start new one
				flushGroup();
				pendingGroup = [m];
				groupStartY = yBar;
				lastLblY = yBar;
			}
		});

		// Flush any remaining group
		flushGroup();
	}

	private grabWatermarks() {
		return (this.record.data?.matches ?? []).map((m: any) => ({
			start: +m.start,
			end: +m.end,
			id: m.track?.raw?.file_id
		}));
	}

	private grabNfp(classification: number) {
		if (!this.record.nfp) return [];

		const data = this.record.nfp;
		if (!data.finalMatches && !data.final_matches) return [];

		// figure out which final matches key
		const finals = data.finalMatches || data.final_matches;
		const matches = finals
			.filter((m: any) => m.classification === classification)
			.map((m: any) => ({
				start: +m.start || +m.start_time || 0,
				end: +m.end || +m.end_time,
				id: m.fileId || m.file_id,
				raw: m
			}));

		return matches;
	}
}
